import { useEffect } from 'react'

const LAST_UPDATED = 'June 3, 2026'

const SECTIONS = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    content: [
      {
        sub: 'Account Information',
        text: 'When you register using Google Sign-In, we receive your name, email address, and profile picture from Google. You may also provide additional profile details such as your city, area, bio, dietary preference, and budget preference.',
      },
      {
        sub: 'Location Information',
        text: 'With your permission, we access your device\'s GPS coordinates to detect your city and show relevant meal partners nearby. We store latitude, longitude, city name, and approximate address for visit tracking and matching purposes. You can deny location access at any time in your browser or device settings.',
      },
      {
        sub: 'Usage Data',
        text: 'We collect information about how you interact with Co-Eater, including pages visited, features used, invites sent and received, and messages exchanged. This helps us improve the product and fix issues.',
      },
      {
        sub: 'Device & Browser Data',
        text: 'We may collect information such as your IP address, browser type, operating system, and device identifiers for security and analytics purposes.',
      },
      {
        sub: 'Push Notification Tokens',
        text: 'If you grant notification permission, we store a Firebase Cloud Messaging (FCM) token associated with your account to send you real-time notifications about new messages and invites.',
      },
    ],
  },
  {
    id: 'how-we-use-information',
    title: '2. How We Use Your Information',
    bullets: [
      'To create and manage your account.',
      'To display your profile to other users looking for meal partners.',
      'To match you with nearby users based on your location, dietary preference, and availability.',
      'To facilitate meal invitations, real-time chat, and post-meal ratings.',
      'To send push notifications about activity relevant to your account.',
      'To track app usage for product analytics and improving our service.',
      'To detect fraud, abuse, and violations of our Terms of Use.',
      'To comply with legal obligations.',
    ],
  },
  {
    id: 'sharing-of-information',
    title: '3. Sharing of Information',
    content: [
      {
        sub: 'Other Users',
        text: 'Your public profile information — name, profile picture, city, area, dietary type, bio, availability status, and ratings — is visible to other registered users on the Discover feed. Private details such as your email address are never shared with other users.',
      },
      {
        sub: 'Service Providers',
        text: 'We use trusted third-party services to operate Co-Eater. These include Google (authentication, maps, and analytics), Firebase (push notifications), Microsoft Azure (media storage), and Sentry (error monitoring). These providers process your data only as necessary to deliver their services and are bound by data processing agreements.',
      },
      {
        sub: 'Legal Requirements',
        text: 'We may disclose your information if required by law, court order, or government authority, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.',
      },
      {
        sub: 'Business Transfers',
        text: 'In the event of a merger, acquisition, or sale of all or part of our assets, your information may be transferred as part of that transaction. We will notify you before your data is subject to a different privacy policy.',
      },
    ],
  },
  {
    id: 'data-storage',
    title: '4. Data Storage & Security',
    text: 'Your data is stored on servers hosted within secure cloud environments. We implement industry-standard security measures including encrypted connections (HTTPS/TLS), access controls, and routine security reviews. However, no method of transmission over the internet is 100% secure. If you believe your account has been compromised, contact us immediately.',
  },
  {
    id: 'data-retention',
    title: '5. Data Retention',
    text: 'We retain your account data for as long as your account is active. If you delete your account, we will delete or anonymise your personal data within 30 days, except where we are required to retain it for legal, tax, or regulatory reasons. Aggregated, anonymised analytics data may be retained indefinitely.',
  },
  {
    id: 'your-rights',
    title: '6. Your Rights',
    bullets: [
      'Access: You may request a copy of the personal data we hold about you.',
      'Correction: You may update your profile information at any time from the Settings page.',
      'Deletion: You may request deletion of your account and associated data by contacting us.',
      'Portability: You may request your data in a portable format.',
      'Withdraw Consent: You may withdraw consent for location access or push notifications at any time through your device settings.',
    ],
  },
  {
    id: 'cookies',
    title: '7. Cookies & Local Storage',
    text: 'Co-Eater uses browser local storage to persist your authentication token and FCM token between sessions. We do not use third-party advertising cookies. Analytics tools (Google Analytics, Microsoft Clarity) may use cookies to understand usage patterns. You can clear local storage and cookies in your browser settings at any time.',
  },
  {
    id: 'children',
    title: '8. Children\'s Privacy',
    text: 'Co-Eater is not intended for anyone under the age of 13. We do not knowingly collect personal data from children under 13. If we become aware that we have collected data from a child under 13, we will promptly delete it. If you believe a child has provided us with personal data, please contact us.',
  },
  {
    id: 'changes',
    title: '9. Changes to This Policy',
    text: 'We may update this Privacy Policy from time to time. We will notify you of significant changes by displaying a notice within the app or by email. The "Last Updated" date at the top of this page reflects the most recent revision. Continued use of Co-Eater after changes are posted constitutes your acceptance of the updated policy.',
  },
  {
    id: 'contact',
    title: '10. Contact Us',
    text: 'If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us at: privacy@coeater.com',
  },
]

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy — Co-Eater'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Read the Co-Eater Privacy Policy to understand how we collect, use, and protect your personal data on our social dining platform.',
      )
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 px-4 py-6 sm:px-8">
    <article className="max-w-3xl mx-auto w-full">
      {/* Header */}
      <header className="mb-8">
        <h1 className="title-font text-3xl font-extrabold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500">Last Updated: {LAST_UPDATED}</p>
        <p className="mt-4 text-gray-600 leading-relaxed">
          Co-Eater (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to protecting your privacy. This Privacy Policy
          explains what information we collect, how we use it, and the choices you have. By using Co-Eater,
          you agree to the collection and use of information as described here.
        </p>
      </header>

      {/* Table of Contents */}
      <nav className="bg-orange-50 rounded-2xl p-5 mb-8 border border-orange-100" aria-label="Table of contents">
        <h2 className="font-bold text-gray-800 mb-3 text-sm">Contents</h2>
        <ol className="space-y-1.5 text-sm">
          {SECTIONS.map(s => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-orange-600 hover:underline">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* Sections */}
      <div className="space-y-8">
        {SECTIONS.map(s => (
          <section key={s.id} id={s.id} className="scroll-mt-4">
            <h2 className="title-font text-lg font-bold text-gray-900 mb-3">{s.title}</h2>
            {s.text && <p className="text-sm text-gray-600 leading-relaxed">{s.text}</p>}
            {s.bullets && (
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600">
                {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            )}
            {s.content && (
              <dl className="space-y-4">
                {s.content.map(item => (
                  <div key={item.sub}>
                    <dt className="font-semibold text-gray-800 text-sm mb-1">{item.sub}</dt>
                    <dd className="text-sm text-gray-600 leading-relaxed">{item.text}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        ))}
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-10 pt-6 pb-10 text-center">
        <p className="title-font font-bold text-gray-800 mb-3">Co-Eater</p>
        <nav className="flex flex-wrap gap-4 justify-center text-xs" aria-label="Footer navigation">
          <a href="/about" className="text-gray-600 hover:text-orange-600 font-medium transition">About</a>
          <span className="text-gray-300">|</span>
          <a href="/privacy-policy" className="text-orange-600 font-semibold">Privacy Policy</a>
          <span className="text-gray-300">|</span>
          <a href="/terms-of-use" className="text-gray-600 hover:text-orange-600 font-medium transition">Terms of Use</a>
        </nav>
        <p className="text-xs text-gray-400 mt-4">&copy; {new Date().getFullYear()} Co-Eater. All rights reserved.</p>
      </footer>
    </article>
    </div>
  )
}
